import { useEffect, useRef, useState, type KeyboardEvent, type RefObject } from 'react';
import { useLocation } from 'react-router-dom';
import { isPlanError } from '../../components/plan/planErrors';
import { UpsellFromError } from '../../components/plan/Upsell';
import { ApiError } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import { countCharacters } from '../../lib/platforms';
import type { AssistantMessageRead, AssistantThreadRead } from '../../types/api';
import { ASSISTANT_DAILY_LIMIT, ASSISTANT_MESSAGE_MAX_LENGTH } from './assistantApi';
import { AssistantActionCards } from './AssistantActionCards';
import { AssistantChanges } from './AssistantChanges';
import { pageContextFor } from './assistantContext';
import { AssistantMessage } from './AssistantMessage';
import { AssistantSuggestions, type Suggestion } from './AssistantSuggestions';
import type { AssistantThreadResult } from './useAssistantThread';

/** The counter appears once it is worth watching. */
const COUNTER_THRESHOLD = ASSISTANT_MESSAGE_MAX_LENGTH - 500;

/** "N messages left today" only shows once it is worth knowing. */
const TURNS_NOTICE_AT = 10;

/** The 429 in words: the owner's sentence, exactly. */
export const LIMIT_REACHED_MESSAGE = `You've used today's ${ASSISTANT_DAILY_LIMIT} messages; it resets at midnight UTC.`;

/** How much of a new reply the live region reads out. */
const ANNOUNCE_LENGTH = 240;

function sendFailureMessage(caught: unknown): string {
  if (caught instanceof ApiError) {
    if (caught.code === 'ASSISTANT_REPLY_PENDING') {
      return 'The assistant is still answering your last message. Give it a moment, then send again.';
    }
    if (caught.code === 'ASSISTANT_LIMIT_REACHED') return LIMIT_REACHED_MESSAGE;
    // The server's sentence carries the reset date; the composer says it too.
    if (caught.code === 'ASSISTANT_CREDITS_EXHAUSTED') return caught.message;
    if (caught.status === 404) {
      return 'This chat is no longer there. Start a new one; your message is still in the box.';
    }
  }
  return getErrorMessage(caught, 'That message did not send.');
}

/** The question a failed reply was answering: its `reply_to`, else the last thing the user said. */
function retryText(messages: readonly AssistantMessageRead[], failed: AssistantMessageRead) {
  const asked = messages.find(
    (message) => message.role === 'user' && message.id === failed.reply_to,
  );
  if (asked) return asked.content;
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message?.role === 'user') return message.content;
  }
  return null;
}

interface ComposerState {
  slot: string;
  text: string;
  error: string | null;
}

interface AssistantConversationProps {
  businessId: string;
  /** Null for a new chat: the thread is created by the first send. */
  threadId: string | null;
  chat: AssistantThreadResult;
  createThread: () => Promise<AssistantThreadRead>;
  /** A new chat got its thread with its first message. */
  onThreadCreated: (thread: AssistantThreadRead) => void;
  /** A message went; the page re-reads its thread list (order and titles move). */
  onSent?: () => void;
  variant: 'panel' | 'page';
  composerRef: RefObject<HTMLTextAreaElement | null>;
}

/**
 * The conversation itself, shared by the bubble's panel and the full page: the
 * transcript, the live step chips while the agent works, the empty state, and
 * the composer. The thread state is owned by the caller (`useAssistantThread`),
 * because the bubble keeps listening while its panel is shut.
 */
export function AssistantConversation({
  businessId,
  threadId,
  chat,
  createThread,
  onThreadCreated,
  onSent,
  variant,
  composerRef,
}: AssistantConversationProps) {
  const location = useLocation();
  const isNew = threadId === null || chat.notFound;
  const slot = isNew ? 'new' : threadId;

  // The composer carries the slot it was typed into; anything from another
  // chat is discarded during render, so switching chats clears it.
  const [composer, setComposer] = useState<ComposerState>({ slot, text: '', error: null });
  const [isSending, setIsSending] = useState(false);
  // A thread created for a message that then failed to send is reused, not orphaned.
  const [spareThread, setSpareThread] = useState<AssistantThreadRead | null>(null);
  // How the last undo went, read out by the live region beside the replies.
  const [undoNotice, setUndoNotice] = useState('');
  const transcriptRef = useRef<HTMLDivElement>(null);

  const isComposerCurrent = composer.slot === slot;
  const draft = isComposerCurrent ? composer.text : '';
  const sendError = isComposerCurrent ? composer.error : null;
  // A 402 on send (the plan lost the assistant mid-session): the Upsell, not a message.
  const [sendPlanError, setSendPlanError] = useState<ApiError | null>(null);

  function setDraft(text: string) {
    setComposer((current) => ({
      slot,
      text,
      error: current.slot === slot ? current.error : null,
    }));
  }

  function setSendError(error: string | null) {
    setComposer((current) => ({
      slot,
      text: current.slot === slot ? current.text : '',
      error,
    }));
  }

  const messages = isNew ? [] : chat.messages;
  const isWaiting = !isNew && chat.awaitingReply && !chat.pollTimedOut;
  const turnsLeft = isNew ? null : chat.turnsLeftToday;
  const isOutOfTurns = turnsLeft !== null && turnsLeft <= 0;
  // Credits are the account's, so a new chat is out of them too.
  const creditsExhausted = chat.creditsExhausted;
  const count = countCharacters(draft);
  const isOverLimit = count > ASSISTANT_MESSAGE_MAX_LENGTH;
  const isBlocked = isSending || isWaiting || isOutOfTurns || creditsExhausted !== null;

  // Keep the newest turn in view: the transcript is its own scroll port.
  useEffect(() => {
    const port = transcriptRef.current;
    if (port) port.scrollTop = port.scrollHeight;
  }, [
    messages.length,
    isWaiting,
    chat.liveSteps.length,
    chat.liveChanges.length,
    chat.liveActions.length,
    chat.expectingFollowup,
  ]);

  async function deliver(text: string): Promise<boolean> {
    const content = text.trim();
    if (!content || countCharacters(content) > ASSISTANT_MESSAGE_MAX_LENGTH || isBlocked) {
      return false;
    }
    setIsSending(true);
    setSendError(null);
    setSendPlanError(null);
    try {
      const pageContext = pageContextFor(location);
      if (isNew) {
        const thread =
          spareThread !== null && spareThread.business_id === businessId
            ? spareThread
            : await createThread();
        setSpareThread(thread);
        await chat.send(content, { pageContext, thread });
        setSpareThread(null);
        onThreadCreated(thread);
      } else {
        await chat.send(content, { pageContext });
      }
      onSent?.();
      return true;
    } catch (caught) {
      if (isPlanError(caught)) setSendPlanError(caught);
      else setSendError(sendFailureMessage(caught));
      return false;
    } finally {
      setIsSending(false);
    }
  }

  async function sendDraft() {
    if (await deliver(draft)) {
      // Only on success: a message that did not go stays in the box. The slot
      // may have moved to the new thread; either way the box is empty now.
      setComposer({ slot: '', text: '', error: null });
    }
  }

  function onComposerKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    // Shift+Enter is a newline; a bare Enter sends. `isComposing` keeps an IME
    // candidate pick from being read as a send.
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    void sendDraft();
  }

  function pickSuggestion(suggestion: Suggestion) {
    if (suggestion.prefill) {
      setDraft(suggestion.text);
      const field = composerRef.current;
      if (field) {
        field.focus();
        // After React writes the value, park the caret at the end of it.
        requestAnimationFrame(() =>
          field.setSelectionRange(field.value.length, field.value.length),
        );
      }
      return;
    }
    void deliver(suggestion.text);
  }

  const lastMessage = messages[messages.length - 1];
  const canRetry =
    lastMessage !== undefined &&
    lastMessage.role === 'assistant' &&
    lastMessage.status === 'failed' &&
    !isWaiting;

  const arrival =
    chat.latestArrivalId === null
      ? null
      : messages.find((message) => message.id === chat.latestArrivalId);
  const announcement =
    arrival === undefined || arrival === null
      ? ''
      : arrival.status === 'failed'
        ? 'The assistant did not finish its reply.'
        : `${arrival.kind === 'followup' ? 'AI assistant update' : 'AI assistant'}: ${arrival.content.slice(0, ANNOUNCE_LENGTH)}`;

  const showEmpty = !chat.isLoading && messages.length === 0 && !isWaiting;

  return (
    <div className={`assistant-convo assistant-convo--${variant}`}>
      <div className="assistant-convo__transcript" ref={transcriptRef}>
        {chat.error && !isNew ? (
          <p className="form__error" role="alert">
            {chat.error}
          </p>
        ) : null}

        {!isNew && chat.isLoading ? (
          <p className="assistant-convo__status" role="status">
            Opening this chat
          </p>
        ) : null}

        {showEmpty ? (
          <AssistantSuggestions
            businessId={businessId}
            onPick={pickSuggestion}
            disabled={isBlocked}
          />
        ) : (
          <ol className="assistant-convo__list">
            {messages.map((message) => (
              <AssistantMessage
                key={message.id}
                message={message}
                businessId={businessId}
                onMark={chat.markProposal}
                onRetry={
                  canRetry && message.id === lastMessage.id
                    ? () => {
                        const text = retryText(messages, message);
                        if (text !== null) void deliver(text);
                      }
                    : undefined
                }
                isRetryDisabled={isBlocked}
                onUndo={chat.undoChange}
                onConfirm={chat.confirmAction}
                onCancel={chat.cancelAction}
                onAnnounce={setUndoNotice}
              />
            ))}
          </ol>
        )}

        {isWaiting ? (
          <div className="assistant-working">
            <p className="assistant-working__line" role="status">
              <span>{chat.liveSteps.length > 0 ? 'Working' : 'Thinking'}</span>
              <span className="assistant-working__dots" aria-hidden="true">
                <span />
                <span />
                <span />
              </span>
            </p>
            {chat.liveSteps.length > 0 ? (
              <ul className="assistant-steps assistant-steps--live" aria-label="Working on">
                {chat.liveSteps.map((step, index) => (
                  <li
                    className={
                      index === chat.liveSteps.length - 1
                        ? 'assistant-step assistant-step--current'
                        : 'assistant-step'
                    }
                    key={index}
                  >
                    {step.label}
                  </li>
                ))}
              </ul>
            ) : null}
            {/* Edits already made this turn; Undo arrives with the finished reply. */}
            <AssistantChanges changes={chat.liveChanges} />
            {/* Cards already made this turn; Confirm arrives with the finished reply. */}
            <AssistantActionCards cards={chat.liveActions} />
          </div>
        ) : null}

        {/* A confirmed card: its follow-up turn reports what happened. */}
        {!isNew && !isWaiting && chat.expectingFollowup ? (
          <div className="assistant-working">
            <p className="assistant-working__line" role="status">
              <span>Working</span>
              <span className="assistant-working__dots" aria-hidden="true">
                <span />
                <span />
                <span />
              </span>
            </p>
          </div>
        ) : null}

        {!isNew && !isWaiting && chat.followupGaveUp ? (
          <p className="assistant-convo__status">
            {chat.watching
              ? 'Still going. The update will show here when it is ready.'
              : 'No update yet. Check the post itself, or ask again.'}
          </p>
        ) : null}

        {!isNew && chat.pollTimedOut ? (
          <p className="assistant-convo__timeout" role="status">
            The assistant is taking too long. Send again to retry.
          </p>
        ) : null}
      </div>

      {/* Replies, undo and card outcomes are announced; the user knows what they sent. */}
      <div className="visually-hidden" aria-live="polite">
        <span>{announcement}</span> <span>{undoNotice}</span>
      </div>

      <div className="assistant-composer">
        <label className="visually-hidden" htmlFor={`assistant-composer-${variant}`}>
          Message the AI assistant
        </label>
        <textarea
          className="field__input assistant-composer__input"
          id={`assistant-composer-${variant}`}
          ref={composerRef}
          rows={variant === 'page' ? 3 : 2}
          value={draft}
          placeholder={
            isWaiting
              ? 'Waiting for the reply'
              : creditsExhausted !== null
                ? 'Out of assistant credits this month'
                : 'Ask or tell the AI assistant'
          }
          // Read-only rather than disabled while a reply is pending, so focus
          // (and Escape) stays inside the panel.
          readOnly={isWaiting}
          aria-disabled={isWaiting}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={onComposerKeyDown}
        />

        <div className="assistant-composer__foot">
          {creditsExhausted !== null ? (
            <p className="assistant-composer__quiet assistant-composer__spent" role="status">
              {creditsExhausted}
            </p>
          ) : turnsLeft !== null && turnsLeft <= TURNS_NOTICE_AT ? (
            <p className="assistant-composer__quiet">
              {isOutOfTurns
                ? LIMIT_REACHED_MESSAGE
                : `${turnsLeft} ${turnsLeft === 1 ? 'message' : 'messages'} left today`}
            </p>
          ) : (
            <p className="assistant-composer__quiet assistant-composer__hint">
              Enter sends · Shift + Enter for a new line
            </p>
          )}

          {count >= COUNTER_THRESHOLD ? (
            <p
              className={
                isOverLimit
                  ? 'assistant-composer__count assistant-composer__count--over'
                  : 'assistant-composer__count'
              }
            >
              {count}/{ASSISTANT_MESSAGE_MAX_LENGTH}
            </p>
          ) : null}

          <button
            className="button button--primary assistant-composer__send"
            type="button"
            disabled={isBlocked || isOverLimit || draft.trim().length === 0}
            onClick={() => void sendDraft()}
          >
            {isSending ? 'Sending' : 'Send'}
          </button>
        </div>

        {sendError && sendError !== creditsExhausted ? (
          <p className="form__error" role="alert">
            {sendError}
          </p>
        ) : null}
        <UpsellFromError error={sendPlanError} />
      </div>
    </div>
  );
}
